<script>
import ChannelMixin from "./channel-mixin"
import ChannelPostMenu from "./channel-post-menu.vue"
import MeetingToolbar from './meeting-toolbar.vue'
import MessageComposer from './message-composer.vue'
import ImageViewer from './image-viewer.vue'

export default {
  name: 'ChannelPrivate',
  mixins: [ChannelMixin],
  components: {ChannelPostMenu, MeetingToolbar, MessageComposer, ImageViewer},
  props: {
    navRelative: {type: Boolean, default: false},
    showTitle: {type: Boolean, default: false},
    contactId: {type: String, default: ''},
  },
  data() {
    return {
      userId: this.$storage.getUserUuid(),
      otherUser: {},
      pendingMeeting: null,
    }
  },

  watch: {
    posts: {
      handler(newValue) {
        // Fallback: extract otherUser from posts when no contactId is available
        if (this.contactId || this.otherUser.email) return
        if (!Object.keys(newValue).length) return
        let otherPost = Object.values(newValue).find(p => p.user_id != this.userId)
        if (otherPost) {
          this.otherUser = {
            first_name: otherPost.first_name,
            last_name: otherPost.last_name,
            username: otherPost.username,
            user_id: otherPost.user_id,
            email: otherPost.email,
          }
        }
      },
      deep: true,
    },
  },

  computed: {
    otherUserDisplay() {
      return `${this.otherUser.first_name || ''} ${this.otherUser.last_name || ''}`.trim() || this.otherUser.username
    },
    isOutgoingPending() {
      let ch = this.$storage.observed.directChannels.find(c => c.id === this.channelId)
      return !!(ch && ch.connection_state === 'outgoing')
    },
    latestMeetingPostIds() {
      // to determine if we have to show the buttons or not
      let latest = {}
      for (let pid of this.orderedPostIds) {
        let uid = this.posts[pid].meeting_ics_uid
        if (uid) latest[uid] = pid
      }
      return new Set(Object.values(latest))
    }
  },

  mounted() {
    // Fetch contact info so the header shows the name immediately,
    // even before any messages exist in the channel.
    if (this.contactId) {
      // Contact info is only needed for the header (name/avatar) - the
      // meeting invitee is resolved server-side from the direct channel.
      let me = this
      this.$api.get(`/community-users/${this.contactId}/`).then(response => {
        let c = response.data
        me.otherUser = {
          first_name: c.first_name,
          last_name: c.last_name,
          username: c.username,
          user_id: c.uuid,
          email: c.email,
          profile_image: c.profile_image,
        }
      }).catch(error => console.error('contact fetch failed:', error))
    }

    let query = {...this.$route.query}

    // Check for meeting query params from appointment-request
    if ('meeting_date' in query) {
      this.pendingMeeting = {
        date: query.meeting_date,
        time: query.meeting_time,
        end: query.meeting_end,
        subject: query.meeting_subject,
      }
      let cleanQuery = {}
      if ('reply_to_post_id' in query) cleanQuery.reply_to_post_id = query.reply_to_post_id
      try { this.$router.replace({query: cleanQuery}) } catch (e) { /* navigation duplicate */ }
      this.sendMeetingPost()
    }

    if ('reply_to_post_id' in query) {
      let me = this
      this.$chat.get(`channels/${this.channelId}/posts/`).then(response => {
        let replyPostId = query.reply_to_post_id
        let post = response.data.posts[replyPostId]

        try {
          this.$router.replace({query: {}})
        } catch (error) { console.error(error) }

        if (post) {
          me.postInput = me.formatReply(post.username, post.message)
          if ('message' in query) {
            me.postInput += `\n${query.message}`
          }
          me.$refs.postInput.focus()
        }
      })
    } else if ('message' in query && !this.pendingMeeting) {
      this.postInput = query.message
      this.$refs.postInput.focus()
    }
  },

  methods: {
    pushMessage(data) {
      // Prefer the uuid - member emails are not exposed via the community API
      let uuid = this.contactId || this.otherUser.user_id
      if (!uuid && !this.otherUser.email) return
      const payload = {
        'title': `${this.$storage.user.username}`,
        'body': data.data.message,
        'url': window.location.href,
      }
      if (uuid) payload.recipient_uuid = uuid
      else payload.recipient_email = this.otherUser.email
      this.$api.post('zpcard/push-notify/', payload)
    },

    postMessage() {
      // overrides channel-mixin.postMessage() to create a pushMessage to user
      console.log('channel-private.postMessage()')
      let me = this
      if (this.postEditId) {
        this.putPost(this.postEditId).then(response => {
          me.pushMessage(response)
        })
      } else {
        this.createPost().then(response => {
          me.pushMessage(response)
        })
      }
    },

    onMeetingRequest(info) {
      this.pendingMeeting = {
        date: info.date,
        time: info.time,
        end: info.end,
        subject: info.subject,
      }
      this.sendMeetingPost()
    },

    sendMeetingPost() {
      if (!this.pendingMeeting) return
      let m = this.pendingMeeting
      this.pendingMeeting = null
      let startDateTime = m.date + 'T' + m.time + ':00'
      let endDateTime = m.date + 'T' + m.end + ':00'

      // No invitee_email: the backend resolves the invitee as the
      // direct-channel counterpart.
      let payload = {
        channel: this.channelId,
        message: `:calendar: Terminanfrage: **${m.date}** ${m.time}\u2013${m.end} Uhr \u2014 ${m.subject}`,
        meeting: {
          startTime: startDateTime,
          endTime: endDateTime,
          subject: m.subject,
          description: `Terminanfrage: ${m.subject}`,
        }
      }
      let me = this
      this.$chat.post('posts/', payload).then(response => {
        me._insertPost(response.data)
        me.pushMessage(response)
      }).catch(error => {
        console.error('meeting request failed:', error.response || error)
        me.pendingMeeting = m
      })
    },

  }
}
</script>

<template>
<div>
  <router-link v-if="showTitle && contactId" :to="`/community/${contactId}`"
    class="d-flex align-items-center gap-2 text-decoration-none text-dark mb-2">
    <img v-if="otherUser.profile_image" :src="otherUser.profile_image" class="c-contact-avatar" />
    <h3 class="mb-0">{{ otherUserDisplay }}</h3>
  </router-link>
  <h3 v-else-if="showTitle">{{ otherUserDisplay }}</h3>
  <div class="card">
    <div class="card-body d-flex flex-column" :class="{'pb-5': navRelative}">

      <div :class="{'c-chat-content': navRelative, 'c-overflow-y': navRelative}">
        <div v-if="prevPostId" class="text-center">
          <span class="btn" @click.prevent="fetchOlderPosts">
            <i class="fas fa-chevron-up"></i>
          </span>
        </div>

        <span v-if="orderedPostIds.length">
          <div v-for="(pid, i) in orderedPostIds" :key="pid" class="mb-2">

            <div v-if="i === 0 || getDayLabel(posts[orderedPostIds[i-1]].created_at) !== getDayLabel(posts[pid].created_at)"
                 class="text-center my-2">
              <small class="text-muted">── {{ getDayLabel(posts[pid].created_at) }} ──</small>
            </div>

            <div class="d-flex">
              <channel-post-menu
              v-if="posts[pid].showMenu"
              :post="posts[pid]"
              :direct="true"
              @like="reactToPost($event, 'heart')"
              @thumbsup="reactToPost($event, '+1')"
              @reply="replyToPost($event)"
              @edit="editPost($event)"
              @delete="deletePost($event)"
              />

              <div
              class="card c-fit" :class="{ 'right-post': posts[pid].user_id == userId }"
              @click="onPostClick(pid, $event)"
              >
                <div class="c-post card-body p-2" v-html="convert2Html(posts[pid].message)">
                </div>
                <div v-if="postImages[pid]" class="px-2 pb-2">
                  <img class="img-thumbnail c-post-img" v-for="(image, idx) in postImages[pid]" :key="image.src" :src="image.src" :alt="image.name" @click.stop="openImageViewer(postImages[pid], idx)" />
                </div>
                <div v-if="postFiles[pid]" class="px-2 pb-2">
                  <a v-for="file in postFiles[pid]" :key="file.src" :href="file.src" target="_blank" class="d-block">
                    <i class="fas fa-paperclip"></i> {{ file.name }}
                  </a>
                </div>
                <meeting-toolbar v-if="posts[pid].meeting_id && latestMeetingPostIds.has(pid)"
                  :ics-uid="posts[pid].meeting_ics_uid"
                  :status="posts[pid].meeting_status"
                  :is-other-user="posts[pid].user_id !== userId"
                  :attachments="posts[pid].attachments || []"
                  :show-actions="latestMeetingPostIds.has(pid)"
                  @status-changed="$set(posts[pid], 'meeting_status', $event.status)"
                  @rescheduled="$set(posts[pid], 'meeting_status', 'pending')"
                  class="px-2 pb-2"
                  @click.native.stop
                />
              </div>

            </div>

            <div class="w-100" :class="{'text-end': posts[pid].user_id == userId}">
              <small class="text-muted">{{ getTimeOnly(posts[pid].created_at) }}</small>
            </div>

            <span v-if="pid in postReactions" class="c-reaction"
            :class="{ 'float-end': posts[pid].user_id == userId }"
            >
              <button v-for="(v, k) in postReactions[pid]" :key="k" class="btn btn-sm btn-outline-light me-1"
                @click.prevent="reactToPost(pid, k)"
              >
                <span style="color: black">{{ emojis[k] || k }}&nbsp;{{ v.size }}</span>
              </button>
            </span>

          </div>
        </span>
        <div v-else class="text-center">
          -- keine Nachrichten --
        </div>
      </div>

    </div>

    <div v-if="isOutgoingPending" class="alert alert-info text-center small mt-2 mb-0">
      <i class="fas fa-clock"></i> Verbindungsanfrage gesendet – wartet auf Annahme.
    </div>
    <message-composer
      v-else
      v-model="postInput"
      :rows="currentRows"
      :nav-relative="navRelative"
      :show-meeting-request="true"
      @send="postMessage"
      @file-selected="pendingFile = $event"
      @meeting-request="onMeetingRequest"
      ref="postInput"
    />

  </div>
  <image-viewer v-if="viewerImages" :images="viewerImages" :start-index="viewerIndex" @close="closeImageViewer" />
</div>
</template>

<style scoped>
.c-contact-avatar {
  height: 2.5rem;
  width: 2.5rem;
  object-fit: cover;
  border-radius: 50%;
}
.c-overflow-y {
    overflow-y: auto;
    scroll-behavior: smooth;
}
.c-fit {
    max-width:90%;
    display: inline-block;
    border-radius: .5rem;
}
.right-post {
    background-color: lemonchiffon;
    margin-left: auto;
}

.c-chat-content {
  height: 60vh;
  overflow-y: auto;
  padding-bottom: 2rem;
}
.c-post-img {
  max-height: 40vh;
  cursor: pointer;
}
</style>
